/**
 * YouTube Live API - Fetches live TV content from Sunday Sports API
 * Automatically updates hero video sections with current live content
 */

class YouTubeLiveAPI {
  constructor() {
    this.apiUrl = "https://app.sundaysports.com/api/v1/live-tv/";
    this.cacheKey = "youtube_live_api_cache";
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes cache
    this.isInitialized = false;
    this.apiFailed = false; // Track if API has failed
    this.failureCooldown = 10 * 60 * 1000; // 10 minutes cooldown after failure
    this.lastFailureTime = 0;
  }

  /**
   * Fetch live TV data from API
   */
  async fetchLiveTVData() {
    // Check if API has failed recently
    if (this.apiFailed && Date.now() - this.lastFailureTime < this.failureCooldown) {
      return null;
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[Live TV] API Error Response:", errorText);

        // Mark API as failed and set cooldown
        this.apiFailed = true;
        this.lastFailureTime = Date.now();

        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();

      try {
        const data = JSON.parse(responseText);

        // Reset failure state on success
        this.apiFailed = false;
        return data;
      } catch (parseError) {
        console.error("[Live TV] JSON Parse Error:", parseError);
        console.error("[Live TV] Raw response that failed to parse:", responseText);

        // Mark API as failed for parse errors too
        this.apiFailed = true;
        this.lastFailureTime = Date.now();

        return null;
      }
    } catch (error) {
      console.error("[Live TV] Error fetching live TV data:", error);

      // Mark API as failed
      this.apiFailed = true;
      this.lastFailureTime = Date.now();

      // Handle different types of errors
      if (error.name === "AbortError") {
        console.error("[Live TV] Request timed out");
      } else if (error.name === "TypeError" && error.message.includes("fetch")) {
        console.error("[Live TV] Network error - check internet connection");
      }

      return null;
    }
  }

  /**
   * Get cached data or fetch fresh data
   */
  async getLiveTVData() {
    // Always fetch fresh data - no caching
    const data = await this.fetchLiveTVData();
    if (data && data.channel) {
      return data;
    }

    // Fallback when API fails
    return {
      channel: {
        url: "https://www.youtube.com/watch?v=o0bCsggW8p0",
        title: "Sunday Sports Live",
        category: "Sports",
        sport: "Live Coverage",
      },
    };
  }

  /**
   * Cache management
   */
  getCachedData() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      return null;
    }
  }

  setCachedData(data) {
    try {
      const cacheData = { data: data, timestamp: Date.now() };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // Ignore cache errors
    }
  }

  isCacheValid(timestamp) {
    return Date.now() - timestamp < this.cacheExpiry;
  }

  /**
   * Clear cached data
   */
  clearCache() {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.error("[Live TV] Error clearing cache:", error);
    }
  }

  /**
   * Reset API failure state
   */
  resetFailureState() {
    this.apiFailed = false;
    this.lastFailureTime = 0;
  }

  /**
   * Get cache info for debugging
   */
  getCacheInfo() {
    const cached = this.getCachedData();
    if (cached) {
      const age = Date.now() - cached.timestamp;
      const isValid = this.isCacheValid(cached.timestamp);
      return { age, isValid, data: cached.data };
    }
    return null;
  }

  /**
   * Extract YouTube video ID from URL
   */
  extractVideoId(url) {
    if (!url) return null;

    const patterns = [/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/, /youtube\.com\/v\/([^&\n?#]+)/];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  }

  /**
   * Convert various timestamp formats to seconds
   */
  convertTimestampToSeconds(timestamp) {
    if (!timestamp) {
      return 0;
    }

    // If already a number, return it
    if (typeof timestamp === "number") {
      return Math.floor(timestamp);
    }

    // If string, parse various formats
    if (typeof timestamp === "string") {
      // Handle formats like "1m30s", "90s", "1:30", "1h30m", etc.
      const timeRegex = /(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/;
      const match = timestamp.match(timeRegex);

      // Only use regex result if at least one group actually matched (has h, m, or s suffix)
      if (match && (match[1] !== undefined || match[2] !== undefined || match[3] !== undefined)) {
        const hours = parseInt(match[1] || "0");
        const minutes = parseInt(match[2] || "0");
        const seconds = parseInt(match[3] || "0");
        const result = hours * 3600 + minutes * 60 + seconds;
        return result;
      }

      // Handle MM:SS or HH:MM:SS format
      if (timestamp.includes(":")) {
        const parts = timestamp.split(":").map((part) => parseInt(part) || 0);
        if (parts.length === 2) {
          // MM:SS format
          const result = parts[0] * 60 + parts[1];
          return result;
        } else if (parts.length === 3) {
          // HH:MM:SS format
          const result = parts[0] * 3600 + parts[1] * 60 + parts[2];
          return result;
        }
      }

      // Try to parse as plain number
      const parsed = parseInt(timestamp);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }

    return 0;
  }

  /**
   * Convert YouTube watch URL to embed URL
   */
  convertToEmbedUrl(watchUrl) {
    // Fix malformed URLs - if the URL has ?start= in the video ID, extract it properly
    let cleanUrl = watchUrl;
    let extractedStartTime = null;

    if (watchUrl.includes("?start=")) {
      // Extract the start parameter before creating URL object
      const startMatch = watchUrl.match(/\?start=(\d+)/);
      if (startMatch) {
        extractedStartTime = startMatch[1];
        // Remove the ?start= part from the URL
        cleanUrl = watchUrl.replace(/\?start=\d+/, "");
      }
    }

    const videoId = this.extractVideoId(cleanUrl);
    if (!videoId) return null;

    // Create base embed URL
    // Note: mute=1 is required for autoplay in most browsers, controls=0 hides controls (we use custom unmute button)
    let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&cc_load_policy=0&enablejsapi=1&playsinline=1`;

    // Add start time if we have one
    if (extractedStartTime) {
      embedUrl += `&start=${extractedStartTime}`;
      return embedUrl;
    }

    // Extract timestamp from URL parameters (for normal URLs)
    try {
      const url = new URL(cleanUrl);

      // Try multiple ways to get the timestamp
      const timestamp = url.searchParams.get("t") || url.searchParams.get("start") || "";

      if (timestamp) {
        // Convert timestamp to seconds if needed
        const startTime = this.convertTimestampToSeconds(timestamp);
        if (startTime > 0) {
          embedUrl += `&start=${startTime}`;
        } else {
          console.warn("[Live TV] Start time converted to 0, not adding start parameter");
        }
      } else {
        console.warn("[Live TV] No timestamp parameter found in URL");
      }
    } catch (error) {
      console.warn("[Live TV] Could not parse URL parameters:", error.message);
      console.warn("[Live TV] Error details:", error);
      console.warn("[Live TV] Stack trace:", error.stack);
    }

    return embedUrl;
  }

  /**
   * Check if a YouTube video is embeddable
   */
  async checkVideoEmbeddable(videoId) {
    try {
      // Use YouTube's oEmbed API to check if video is embeddable
      const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
      const response = await fetch(oEmbedUrl, {
        method: "GET",
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) {
        const data = await response.json();
        return true;
      } else {
        // Only mark as unembeddable if it's a 403 (forbidden) or 404 (not found)
        // Other errors (network, timeout) should not prevent embedding
        if (response.status === 403 || response.status === 404) {
          console.warn("[Live TV] Video not embeddable:", response.status);
          return false;
        } else {
          // For other errors (network issues, etc.), assume video is embeddable
          console.warn("[Live TV] Could not verify embeddability (status:", response.status, "), assuming embeddable");
          return true;
        }
      }
    } catch (error) {
      // Network errors or timeouts should not prevent embedding
      // Only fail if we can definitively determine the video is unembeddable
      console.warn("[Live TV] Could not check video embeddability (network/timeout error), assuming embeddable:", error.message);
      return true;
    }
  }

  /**
   * Show video unavailable message
   */
  showVideoUnavailable(section, videoId) {
    const iframe = section.querySelector(".hero-video__youtube");
    if (!iframe) return;

    // Hide iframe and show unavailable message
    iframe.style.display = "none";

    // Create or update unavailable message
    let unavailableDiv = section.querySelector(".hero-video__unavailable");
    if (!unavailableDiv) {
      unavailableDiv = document.createElement("div");
      unavailableDiv.className = "hero-video__unavailable";
      unavailableDiv.innerHTML = `
        <div class="hero-video__unavailable-content">
          <div class="hero-video__unavailable-icon">📺</div>
          <h3>Video Unavailable</h3>
          <p>This video cannot be embedded due to privacy settings.</p>
          <a href="https://www.youtube.com/watch?v=${videoId}" target="_blank" class="hero-video__unavailable-link">
            Watch on YouTube
          </a>
        </div>
      `;
      const videoWrapper = section.querySelector(".hero-video__video-wrapper");
      if (videoWrapper) {
        videoWrapper.appendChild(unavailableDiv);
      } else {
        // Fallback: append to section if video-wrapper doesn't exist
        section.appendChild(unavailableDiv);
      }
    }

    // Hide loading placeholder if it exists
    const loadingPlaceholder = section.querySelector(".hero-video__loading-placeholder");
    if (loadingPlaceholder) {
      loadingPlaceholder.style.display = "none";
    }
  }

  /**
   * Update hero video section with live TV content
   */
  async updateHeroVideo() {
    try {
      const liveData = await this.getLiveTVData();
      if (!liveData || !liveData.channel) {
        console.warn("[Live TV] No valid channel data available, keeping current video");
        return;
      }

      const heroSections = document.querySelectorAll('.hero-video[data-live-tv="true"]');
      if (heroSections.length === 0) {
        return;
      }

      const { channel } = liveData;

      // Validate channel data
      if (!channel.url) {
        console.error("[Live TV] Channel data missing URL:", channel);
        return;
      }

      const videoId = this.extractVideoId(channel.url);
      if (!videoId) {
        console.error("[Live TV] Could not extract video ID from URL:", channel.url);
        return;
      }

      // Check if video is embeddable
      const isEmbeddable = await this.checkVideoEmbeddable(videoId);
      if (!isEmbeddable) {
        console.warn("[Live TV] Video is not embeddable, showing unavailable message");
        // Show unavailable message for all hero sections
        heroSections.forEach((section) => this.showVideoUnavailable(section, videoId));
        return;
      }

      const embedUrl = this.convertToEmbedUrl(channel.url);

      if (!embedUrl) {
        console.error("[Live TV] Could not convert URL to embed format:", channel.url);
        return;
      }

      heroSections.forEach((section) => {
        const iframe = section.querySelector(".hero-video__youtube");
        if (!iframe) {
          console.warn("[Live TV] No YouTube iframe found in hero section");
          return;
        }

        // Check if the video has actually changed to prevent unnecessary reloads
        // Compare both video ID and embed URL to detect timestamp changes
        const currentVideoId = iframe.getAttribute("data-video-id");
        const currentEmbedUrl = iframe.src;

        if (currentVideoId === videoId && currentEmbedUrl === embedUrl) {
          return;
        }

        // Update iframe source
        iframe.src = embedUrl;
        iframe.setAttribute("data-video-id", videoId);
        // Expose a consistent selector for trackers
        iframe.setAttribute("data-video-type", "youtube");
        iframe.title = channel.title || "Live TV";

        // Show the iframe and hide loading placeholder
        iframe.style.display = "block";
        const loadingPlaceholder = section.querySelector(".hero-video__loading-placeholder");
        if (loadingPlaceholder) {
          loadingPlaceholder.style.display = "none";
        }

        // Force closed captions off. cc_load_policy=0 isn't always honored by
        // YouTube, so once the player loads, tell it to drop the captions module
        // (same postMessage JS API the unmute/volume controls use).
        iframe.addEventListener("load", () => {
          const disableCaptions = () => {
            ["captions", "cc"].forEach((module) => {
              iframe.contentWindow.postMessage(
                JSON.stringify({ event: "command", func: "unloadModule", args: [module] }),
                "*",
              );
            });
          };
          // Retry a few times since the player initializes asynchronously.
          [500, 1500, 3000].forEach((delay) => setTimeout(disableCaptions, delay));
        });

        // Update any title elements in the section
        const titleElements = section.querySelectorAll("[data-live-tv-title]");
        titleElements.forEach((element) => {
          element.textContent = channel.title || "";
        });

        // Update any category elements
        const categoryElements = section.querySelectorAll("[data-live-tv-category]");
        categoryElements.forEach((element) => {
          element.textContent = channel.category || "";
        });

        // Update any sport elements
        const sportElements = section.querySelectorAll("[data-live-tv-sport]");
        sportElements.forEach((element) => {
          element.textContent = channel.sport || "";
        });
      });
    } catch (error) {
      console.error("[Live TV] Error updating hero video:", error);
      // Don't change video on error - keep current state
    }
  }

  /**
   * Check for content updates without reloading iframe
   */
  async checkForContentUpdates() {
    try {
      const liveData = await this.getLiveTVData();
      if (!liveData || !liveData.channel) {
        return;
      }

      const { channel } = liveData;
      const newVideoId = this.extractVideoId(channel.url);

      // Check if video has changed
      const heroSections = document.querySelectorAll('.hero-video[data-live-tv="true"]');
      heroSections.forEach((section) => {
        const iframe = section.querySelector(".hero-video__youtube");
        if (!iframe) return;

        const currentVideoId = iframe.getAttribute("data-video-id");

        // Only update if the video ID has actually changed
        if (currentVideoId !== newVideoId) {
          this.updateHeroVideo();
        }
      });
    } catch (error) {
      console.error("[Live TV] Error checking for content updates:", error);
    }
  }

  /**
   * Initialize the live TV API
   */
  async init() {
    // Prevent multiple instances
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    // Initial update on page load only
    await this.updateHeroVideo();
  }
}

// Global singleton to prevent multiple instances
let globalLiveAPI = null;

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  if (!globalLiveAPI) {
    globalLiveAPI = new YouTubeLiveAPI();
    globalLiveAPI.init();

    // Expose debugging methods globally
    window.liveTVDebug = {
      clearCache: () => globalLiveAPI.clearCache(),
      getCacheInfo: () => globalLiveAPI.getCacheInfo(),
      forceUpdate: () => globalLiveAPI.updateHeroVideo(),
      resetFailure: () => globalLiveAPI.resetFailureState(),
      getFailureState: () => ({
        apiFailed: globalLiveAPI.apiFailed,
        lastFailureTime: globalLiveAPI.lastFailureTime,
        cooldownRemaining: globalLiveAPI.apiFailed ? Math.max(0, globalLiveAPI.failureCooldown - (Date.now() - globalLiveAPI.lastFailureTime)) : 0,
      }),
    };
  }
});

// Also initialize immediately if DOM is already loaded
if (document.readyState !== "loading") {
  if (!globalLiveAPI) {
    globalLiveAPI = new YouTubeLiveAPI();
    globalLiveAPI.init();

    // Expose debugging methods globally
    window.liveTVDebug = {
      clearCache: () => globalLiveAPI.clearCache(),
      getCacheInfo: () => globalLiveAPI.getCacheInfo(),
      forceUpdate: () => globalLiveAPI.updateHeroVideo(),
      resetFailure: () => globalLiveAPI.resetFailureState(),
      getFailureState: () => ({
        apiFailed: globalLiveAPI.apiFailed,
        lastFailureTime: globalLiveAPI.lastFailureTime,
        cooldownRemaining: globalLiveAPI.apiFailed ? Math.max(0, globalLiveAPI.failureCooldown - (Date.now() - globalLiveAPI.lastFailureTime)) : 0,
      }),
    };
  }
}
